import { useEffect, useRef, useState, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { useLocation, useNavigate } from "react-router-dom";
import { baseUrl } from "../../../utils/httpService";     
import axios from "axios";
import "swiper/css";
import "./reel.css";
import { buildYouTubeUrl } from "./reelcmp";
import gif from "../../../assets/google-follow-us.gif"

function Reel() {
  const [reelsData, setReelsData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [fetchingNextPage, setFetchingNextPage] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const swiperRef = useRef(null);
  const iframeRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch shorts — pass video_id as param when opening a specific video
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const initialVideoId = location.state?.video_id || urlParams.get("v");

    // Build API URL: include video_id param when a specific video is requested
    const apiUrl = initialVideoId
      ? `${baseUrl}youtube-shorts?page=1&limit=10&video_id=${initialVideoId}`
      : `${baseUrl}youtube-shorts?page=1&limit=10`;

    axios
      .get(apiUrl)
      .then((res) => {
        if (res.data?.data) {
          const data = res.data.data;
          setReelsData(data);

          if (initialVideoId) {
            const index = data.findIndex((item) => item.video_id === initialVideoId);
            if (index !== -1) {
              setActiveIndex(index);
            }
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [location.search]);

  // Mousewheel — works even when cursor is over iframe
  useEffect(() => {
    let throttled = false;
    const onWheel = (e) => {
      if (throttled) return;
      throttled = true;
      if (e.deltaY > 0) swiperRef.current?.slideNext();
      else swiperRef.current?.slidePrev();
      setTimeout(() => (throttled = false), 800);
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, []);

  // Update active slide index + sync URL with currently playing video_id
  const onSlideChange = useCallback(
    (swiper) => {
      const newIndex = swiper.activeIndex;
      setActiveIndex(newIndex);
      setIsPlaying(true);

      // Update URL so browser shows the current video's id
      setReelsData((prev) => {
        const currentVideo = prev[newIndex];
        if (currentVideo?.video_id) {
          window.history.replaceState(null, "", `/reel?v=${currentVideo.video_id}`);
        }
        return prev;
      });
    },
    []
  );

  // Fetch more reels for pagination
  const fetchMoreReels = useCallback(() => {
    if (fetchingNextPage || !hasMore) return;
    setFetchingNextPage(true);
    const nextPage = page + 1;
    axios
      .get(`${baseUrl}youtube-shorts?page=${nextPage}&limit=10`)
      .then((res) => {
        if (res.data?.data && res.data.data.length > 0) {
          setReelsData((prev) => [...prev, ...res.data.data]);
          setPage(nextPage);
        } else {
          setHasMore(false);
        }
        setFetchingNextPage(false);
      })
      .catch(() => {
        setFetchingNextPage(false);
      });
  }, [page, fetchingNextPage, hasMore]);

  // Trigger pagination pre-fetching when index approaches current end
  useEffect(() => {
    if (reelsData.length > 0 && activeIndex >= reelsData.length - 2) {
      fetchMoreReels();
    }
  }, [activeIndex, reelsData.length, fetchMoreReels]);

  // Helpers
  const currentItem = reelsData[activeIndex];
  const shareUrl = currentItem
    ? `https://www.youtube.com/shorts/${currentItem.video_id}`
    : "";

  const embedUrl = (id) =>
    buildYouTubeUrl(id, {
      autoplay: 1,
      loop: 1,
      rel: 0,
      modestbranding: 1,
      playsinline: 1,
      controls: 0,
      fs: 0,
      iv_load_policy: 0,
      cc_load_policy: 0,
      enablejsapi: 1,
      origin: window.location.origin,
    });

  const togglePlay = () => {
    if (!iframeRef.current) return;
    const targetState = !isPlaying;
    const command = targetState ? "playVideo" : "pauseVideo";
    iframeRef.current.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: command, args: "" }),
      "*"
    );
    setIsPlaying(targetState);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Loading / empty
  if (loading) {
    return (
      <div className="reel-page">
        <div className="reel-spinner" />
      </div>
    );
  }

  if (!reelsData.length) {
    return (
      <div className="reel-page">
        <p style={{ color: "#666" }}>No shorts available</p>
      </div>
    );
  }

  return (
    <div className="reel-page">
      {/* Mobile Header Bar */}
      <div className="reel-header-bar">
        <span className="reel-header-title">Short Videos</span>
      </div>

      {/* Back */}
      <button className="reel-back" onClick={() => window.history.back()}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18L9 12L15 6" />
        </svg>
      </button>

      {/* Google News Follow Link */}
      <a
        href="https://www.google.com/preferences/source?q=sandesh.com"
        target="_blank"
        rel="noreferrer"
        className="reel-google-news"
      >
        <img src={gif} alt="Follow us on Google News" style={{ height: "38px", width: "auto" }} />
      </a>

      <div className="reel-container">
        {/* Player */}
        <div className="reel-player">
          <Swiper
            direction="vertical"
            slidesPerView={1}
            initialSlide={activeIndex}
            onSlideChange={onSlideChange}
            onSwiper={(s) => (swiperRef.current = s)}
            className="reel-swiper"
          >
            {reelsData.map((item, i) => (
              <SwiperSlide key={item.id}>
                <div className="reel-slide">
                  <img src={item.thumbnail_url} alt="" className="reel-thumb" loading="lazy" />
                  {i === activeIndex && (
                    <>
                      <iframe
                        ref={iframeRef}
                        src={embedUrl(item.video_id)}
                        title={item.title}
                        className="reel-iframe"
                        frameBorder="0"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                      />
                      <div className="reel-video-overlay" onClick={togglePlay} />
                    </>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Right side controls */}
        <div className="reel-side-controls">
          {/* Share */}
          <div className="reel-share-buttons">
            {/* WhatsApp */}
            <a
              className="reel-share-btn whatsapp"
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noreferrer"
              title="Share on WhatsApp"
            >
              <div className="reel-share-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <span>વોટ્સએપ</span>
            </a>

            {/* Facebook */}
            <a
              className="reel-share-btn facebook"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noreferrer"
              title="Share on Facebook"
            >
              <div className="reel-share-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </div>
              <span>ફેસબુક</span>
            </a>

            {/* Twitter / X */}
            <a
              className="reel-share-btn twitter"
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noreferrer"
              title="Share on X"
            >
              <div className="reel-share-circle">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </div>
              <span>ટ્વિટર</span>
            </a>

            {/* Copy Link */}
            <button
              className={`reel-share-btn copy-link ${copied ? "copied" : ""}`}
              onClick={copyLink}
              title="Copy Link"
            >
              <div className="reel-share-circle">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <span>{copied ? "કોપી થઈ!" : "કોપી લિંક"}</span>
            </button>
          </div>
        </div>

        {/* Up / Down Navigation */}
        <div className="reel-nav">
          <button
            className="reel-nav-btn"
            onClick={() => swiperRef.current?.slidePrev()}
            disabled={activeIndex === 0}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5"></line>
              <polyline points="5 12 12 5 19 12"></polyline>
            </svg>
          </button>
          <button
            className="reel-nav-btn"
            onClick={() => swiperRef.current?.slideNext()}
            disabled={activeIndex === reelsData.length - 1}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <polyline points="19 12 12 19 5 12"></polyline>
            </svg>
          </button>
        </div>
      </div>


    </div>
  );
}

export default Reel;